import React, { useState } from 'react';
import PropTypes from 'prop-types';
import QuestionRenderer from '../../onboarding/question_renderer';

const QuestionBuilder = ({ 
  question, 
  index, 
  onChange, 
  onDelete, 
  availableConditionalQuestions = [],
  errors = {},
  importableFields = {}
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!question.id); // New questions start expanded

  const handleFieldChange = (field, value) => {
    const updatedQuestion = { ...question, [field]: value };
    
    // Clear conditional fields if question type changes to non-conditional types
    if (field === 'question_type' && !['radio', 'checkbox', 'dropdown'].includes(value)) {
      updatedQuestion.conditional_question_id = null;
      updatedQuestion.conditional_answer = '';
      updatedQuestion.conditional_operator = 'equals';
    }
    
    // Clear options if switching from option-based to non-option-based type
    if (field === 'question_type' && !['radio', 'checkbox', 'dropdown'].includes(value)) {
      updatedQuestion.options = [];
      updatedQuestion.has_other_option = false;
      updatedQuestion.other_option_label = '';
    }
    
    onChange(updatedQuestion);
  };

  const handleOptionsChange = (options) => {
    handleFieldChange('options', options);
  };

  const addOption = () => {
    const newOptions = [...optionsArray, ''];
    handleOptionsChange(newOptions);
  };

  const updateOption = (index, value) => {
    const newOptions = [...optionsArray];
    newOptions[index] = value;
    handleOptionsChange(newOptions);
  };

  const removeOption = (index) => {
    const newOptions = [...optionsArray];
    newOptions.splice(index, 1);
    handleOptionsChange(newOptions);
  };

  const needsOptions = ['radio', 'checkbox', 'dropdown'].includes(question.question_type);
  const canBeConditional = true; // All questions can be conditional in our system
  
  // Parse options if they come as JSON string
  const getOptionsArray = () => {
    if (Array.isArray(question.options)) {
      return question.options;
    }
    if (typeof question.options === 'string') {
      try {
        const parsed = JSON.parse(question.options);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };
  
  const optionsArray = getOptionsArray();

  const getAvailableConditionalAnswers = () => {
    if (!question.conditional_question_id) return [];
    
    const conditionalQuestion = availableConditionalQuestions.find(
      q => q.id === question.conditional_question_id
    );
    
    if (!conditionalQuestion) return [];
    
    if (['radio', 'dropdown'].includes(conditionalQuestion.question_type)) {
      return Array.isArray(conditionalQuestion.options) ? conditionalQuestion.options : [];
    } else if (conditionalQuestion.question_type === 'checkbox') {
      return Array.isArray(conditionalQuestion.options) ? conditionalQuestion.options : [];
    }
    
    // For text/textarea, return common values or allow custom input
    return [];
  };

  return (
    <div className={`question-builder ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="question-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="question-summary">
          <span className="question-number">#{index + 1}</span>
          <span className="question-text">
            {question.question_text || 'New Question'}
          </span>
          <span className="question-type badge badge-secondary ml-2">
            {question.question_type || 'text'}
          </span>
          {question.required && (
            <span className="badge badge-danger ml-1">Required</span>
          )}
          {question.conditional_question_id && (
            <span className="badge badge-info ml-1">Conditional</span>
          )}
        </div>
        <div className="question-actions">
          <button
            type="button"
            className="btn btn-link btn-sm text-danger"
            onClick={(e) => {
              e.stopPropagation();
              swal({
                title: 'Delete this question?',
                text: 'This action cannot be undone.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it',
                cancelButtonText: 'Cancel'
              }).then(result => {
                if (result.value) {
                  onDelete();
                }
              });
            }}
            title="Delete Question"
          >
            <i className="fa fa-trash"></i>
          </button>
          <i className={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
        </div>
      </div>

      {isExpanded && (
        <div className="question-content">
          <div className="row">
            <div className={`col-md-${showPreview ? '6' : '12'}`}>
              <div className="form-group">
                <label className="form-label">Question Text</label>
                <input
                  type="text"
                  className={`form-control ${errors.question_text ? 'is-invalid' : ''}`}
                  value={question.question_text || ''}
                  onChange={(e) => handleFieldChange('question_text', e.target.value)}
                  placeholder="Enter your question..."
                />
                {errors.question_text && (
                  <div className="invalid-feedback">{errors.question_text}</div>
                )}
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Question Type</label>
                    <select
                      className="form-control"
                      value={question.question_type || 'text'}
                      onChange={(e) => handleFieldChange('question_type', e.target.value)}
                    >
                      <option value="text">Text Input</option>
                      <option value="textarea">Long Text (Textarea)</option>
                      <option value="radio">Radio buttons (allow one selection)</option>
                      <option value="checkbox">Checkboxes (allow multiple selections)</option>
                      <option value="dropdown">Dropdown</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">
                      Field Mapping
                      <small className="text-muted ml-1">(optional)</small>
                    </label>
                    <select
                      className="form-control"
                      value={question.importable_field_key || ''}
                      onChange={(e) => handleFieldChange('importable_field_key', e.target.value || null)}
                    >
                      <option value="">No field mapping</option>
                      {Object.entries(importableFields).map(([groupName, fields]) => (
                        <optgroup key={groupName} label={groupName}>
                          {fields.map(([label, value]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <small className="form-text text-muted">
                      Link this question to a database field for automatic import functionality
                    </small>
                  </div>
                </div>
              </div>

              {needsOptions && (
                <div className="form-group">
                  <label className="form-label">Options</label>
                  {optionsArray.map((option, optionIndex) => (
                    <div key={optionIndex} className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={option}
                        onChange={(e) => updateOption(optionIndex, e.target.value)}
                        placeholder={`Option ${optionIndex + 1}`}
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeOption(optionIndex)}
                          title="Remove option"
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={addOption}
                    >
                      <i className="fa fa-plus"></i> Add Option
                    </button>
                    {errors.options && (
                      <div className="text-danger mt-1">{errors.options}</div>
                    )}
                  </div>

                  {question.question_type !== 'dropdown' && (
                    <div className="form-check mt-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`other_option_${question.id || index}`}
                        checked={question.has_other_option || false}
                        onChange={(e) => handleFieldChange('has_other_option', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`other_option_${question.id || index}`}>
                        Include "Other" option
                      </label>
                    </div>
                  )}

                  {question.has_other_option && (
                    <div className="form-group mt-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={question.other_option_label || ''}
                        onChange={(e) => handleFieldChange('other_option_label', e.target.value)}
                        placeholder="Other option label (default: Other)"
                      />
                    </div>
                  )}
                </div>
              )}

              {canBeConditional && availableConditionalQuestions.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Conditional Logic</label>
                  <div className="border rounded p-3">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`conditional_${question.id || index}`}
                        checked={!!question.conditional_question_id}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFieldChange('conditional_question_id', availableConditionalQuestions[0]?.id);
                          } else {
                            const updatedQuestion = { 
                              ...question, 
                              conditional_question_id: null,
                              conditional_answer: '',
                              conditional_operator: 'equals'
                            };
                            onChange(updatedQuestion);
                          }
                        }}
                      />
                      <label className="form-check-label" htmlFor={`conditional_${question.id || index}`}>
                        Only show this question based on a previous answer
                      </label>
                    </div>

                    {question.conditional_question_id && (
                      <>
                        <div className="form-group mb-2">
                          <label className="form-label form-label-sm">Show when</label>
                          <select
                            className="form-control form-control-sm"
                            value={question.conditional_question_id || ''}
                            onChange={(e) => handleFieldChange('conditional_question_id', parseInt(e.target.value))}
                          >
                            {availableConditionalQuestions.map(q => (
                              <option key={q.id} value={q.id}>
                                {q.question_text}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group mb-2">
                          <label className="form-label form-label-sm">Condition</label>
                          <select
                            className="form-control form-control-sm"
                            value={question.conditional_operator || 'equals'}
                            onChange={(e) => handleFieldChange('conditional_operator', e.target.value)}
                          >
                            <option value="equals">equals</option>
                            <option value="includes">includes</option>
                            <option value="not_equals">does not equal</option>
                            <option value="not_includes">does not include</option>
                          </select>
                        </div>

                        <div className="form-group mb-0">
                          <label className="form-label form-label-sm">Value</label>
                          {getAvailableConditionalAnswers().length > 0 ? (
                            <select
                              className="form-control form-control-sm"
                              value={question.conditional_answer || ''}
                              onChange={(e) => handleFieldChange('conditional_answer', e.target.value)}
                            >
                              <option value="">Select an answer...</option>
                              {getAvailableConditionalAnswers().map((answer, idx) => (
                                <option key={idx} value={answer}>{answer}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={question.conditional_answer || ''}
                              onChange={(e) => handleFieldChange('conditional_answer', e.target.value)}
                              placeholder="Enter the answer value..."
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {showPreview && (
              <div className="col-md-6">
                <div className="preview-panel">
                  <h6 className="preview-title">
                    <i className="fa fa-eye"></i> Preview
                  </h6>
                  <div className="preview-content">
                    <QuestionRenderer
                      question={{
                        ...question,
                        id: question.id || `preview_${index}`,
                        options: optionsArray,
                        parsed_options: optionsArray
                      }}
                      value=""
                      onChange={() => {}} // Preview only
                      error=""
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id={`required_${question.id || index}`}
                checked={question.required || false}
                onChange={(e) => handleFieldChange('required', e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`required_${question.id || index}`}>
                Required
              </label>
            </div>
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id={`preview_${question.id || index}`}
                checked={showPreview}
                onChange={(e) => setShowPreview(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor={`preview_${question.id || index}`}>
                Preview
              </label>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .question-builder {
          background: white;
        }
        
        .question-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          cursor: pointer;
          border-bottom: 1px solid transparent;
          transition: all 0.2s ease;
        }
        
        .question-header:hover {
          background-color: #f8f9fa;
        }
        
        .expanded .question-header {
          border-bottom-color: #dee2e6;
        }
        
        .question-summary {
          display: flex;
          align-items: center;
          flex: 1;
        }
        
        .question-number {
          font-weight: bold;
          color: #6c757d;
          margin-right: 0.5rem;
        }
        
        .question-text {
          flex: 1;
          margin-right: 0.5rem;
          font-weight: 500;
        }
        
        .question-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .question-content {
          padding: 1rem;
          border-top: 1px solid #dee2e6;
        }
        
        .preview-panel {
          border: 1px solid #e9ecef;
          border-radius: 0.25rem;
          padding: 1rem;
          background: #f8f9fa;
        }
        
        .preview-title {
          color: #6c757d;
          font-size: 0.875rem;
          margin-bottom: 0.75rem;
        }
        
        .preview-content {
          background: white;
          padding: 1rem;
          border-radius: 0.25rem;
          border: 1px solid #dee2e6;
        }
        
        .form-label-sm {
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.25rem;
        }
        
        .collapsed .question-content {
          display: none;
        }
      `}</style>
    </div>
  );
};

QuestionBuilder.propTypes = {
  question: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  availableConditionalQuestions: PropTypes.array,
  errors: PropTypes.object,
  importableFields: PropTypes.object
};

export default QuestionBuilder;